const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

export class MailHubContractError extends Error {
    constructor(message) {
        super(message);
        this.name = "MailHubContractError";
    }
}

export function contractError(message) {
    return new MailHubContractError(message);
}

export function isMailHubContractError(error) {
    return error?.name === "MailHubContractError";
}

export function formatMailHubError(error, fallback) {
    if (isMailHubContractError(error)) {
        return error.message;
    }
    return error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || fallback;
}

export function readMailHubData(response, context) {
    if (!isPlainObject(response?.data) || !hasOwn(response.data, "data")) {
        throw contractError(`${context} 响应缺少 data 字段`);
    }
    return response.data.data;
}

function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function requireObject(value, context) {
    if (!isPlainObject(value)) {
        throw contractError(`${context} 必须是对象`);
    }
    return value;
}

function requireArray(value, context) {
    if (!Array.isArray(value)) {
        throw contractError(`${context} 必须是数组`);
    }
    return value;
}

function readField(object, key, context) {
    if (!hasOwn(object, key)) {
        throw contractError(`${context}.${key} 缺失`);
    }
    return object[key];
}

function requireString(value, context) {
    if (typeof value !== "string" || value.length === 0) {
        throw contractError(`${context} 必须是非空字符串`);
    }
    return value;
}

function requireStringField(object, key, context) {
    return requireString(readField(object, key, context), `${context}.${key}`);
}

function requireNullableString(value, context) {
    if (value === null) {
        return value;
    }
    if (typeof value !== "string") {
        throw contractError(`${context} 必须是字符串或 null`);
    }
    return value;
}

function requireNullableStringField(object, key, context) {
    return requireNullableString(readField(object, key, context), `${context}.${key}`);
}

function requireBoolean(value, context) {
    if (typeof value !== "boolean") {
        throw contractError(`${context} 必须是布尔值`);
    }
    return value;
}

function requireBooleanField(object, key, context) {
    return requireBoolean(readField(object, key, context), `${context}.${key}`);
}

function requireNumber(value, context) {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw contractError(`${context} 必须是数字`);
    }
    return value;
}

function requireNumberField(object, key, context) {
    return requireNumber(readField(object, key, context), `${context}.${key}`);
}

function requireNullableNumber(value, context) {
    if (value === null) {
        return value;
    }
    return requireNumber(value, context);
}

function requireNullableNumberField(object, key, context) {
    return requireNullableNumber(readField(object, key, context), `${context}.${key}`);
}

function requireNonNegativeInteger(value, context) {
    if (!Number.isInteger(value) || value < 0) {
        throw contractError(`${context} 必须是非负整数`);
    }
    return value;
}

function requireNonNegativeIntegerField(object, key, context) {
    return requireNonNegativeInteger(readField(object, key, context), `${context}.${key}`);
}

function requirePositiveIntegerField(object, key, context) {
    const value = readField(object, key, context);
    if (!Number.isInteger(value) || value < 1) {
        throw contractError(`${context}.${key} 必须是正整数`);
    }
    return value;
}

function requireEnum(value, allowed, context) {
    if (!allowed.includes(value)) {
        throw contractError(`${context} 枚举值不受前端契约支持: ${String(value)}`);
    }
    return value;
}

function requireEnumField(object, key, allowed, context) {
    return requireEnum(readField(object, key, context), allowed, `${context}.${key}`);
}

function requireNullableEnumField(object, key, allowed, context) {
    const value = readField(object, key, context);
    if (value === null) {
        return value;
    }
    return requireEnum(value, allowed, `${context}.${key}`);
}

function requireNullableDateString(value, context) {
    if (value === null) {
        return value;
    }
    if (typeof value !== "string" || Number.isNaN(Date.parse(value))) {
        throw contractError(`${context} 必须是合法时间字符串或 null`);
    }
    return value;
}

function requireDateStringField(object, key, context) {
    const value = readField(object, key, context);
    if (typeof value !== "string" || Number.isNaN(Date.parse(value))) {
        throw contractError(`${context}.${key} 必须是合法时间字符串`);
    }
    return value;
}

function requireNullableDateStringField(object, key, context) {
    return requireNullableDateString(readField(object, key, context), `${context}.${key}`);
}

function requireStringArray(value, context) {
    const items = requireArray(value, context);
    items.forEach((item, index) => requireString(item, `${context}[${index}]`));
    return items;
}

function requireNullableObjectField(object, key, context) {
    const value = readField(object, key, context);
    if (value === null) {
        return value;
    }
    return requireObject(value, `${context}.${key}`);
}

function requirePresentField(object, key, context) {
    readField(object, key, context);
}

const MAILBOX_PROCESSING_MODES = ["observe_only", "controlled", "full_processing"];
const MAILBOX_TYPES = ["user_mailbox", "system_mailbox"];
const PROCESSING_CAPABILITIES = [
    "mvt_parse",
    "cpm_parse",
    "ffm_parse",
    "fwb_parse",
    "cgo_attachment_parse",
    "ocr_external_call",
    "business_judgment",
    "ucm_forward",
    "fuel_forward",
    "hkg_cargo_attachment_forward",
    "auto_forward",
    "auto_send",
];
const WORK_ITEM_STATUSES = ["pending", "in_progress", "dispatched", "completed", "cancelled", "failed", "dead"];
const PROCESSING_STATUSES = ["new", "normalized", "classified", "dispatched", "skipped", "failed", "retried", "terminated"];
const WORK_ITEM_PROCESSING_MODES = ["deterministic", "ai_assisted", "manual"];
const EXECUTION_KINDS = ["LOCAL", "AGENT_CALLBACK", "EXTERNAL_POLL"];
const POOL_PROFILES = ["IO_BOUND", "CPU_BOUND", "LLM_CALL", "DEFAULT"];
const CLASSIFICATION_SCOPES = ["global", "organization", "mailbox"];
const CLASSIFICATION_FIELDS = ["subject", "from_address", "from_display", "to_address", "attachment_filename", "has_attachment"];
const CLASSIFICATION_OPERATORS = ["contains", "not_contains", "equals", "not_equals", "starts_with", "ends_with", "regex", "contains_any", "equals_any", "is_true", "is_false"];
const CLASSIFICATION_KINDS = ["primary_route", "secondary_signal"];
const PIPELINE_POLICY_STATUSES = ["active", "draining", "paused", "disabled"];
const PIPELINE_GROUPS = ["core_fact", "optional_derivative", "side_effect"];
const PIPELINE_TARGET_KINDS = ["item_type", "transition"];
const PIPELINE_POLICY_SOURCES = ["override", "default", "read_failed"];
const PIPELINE_STAGES = ["transition", "worker", "create"];
const BJA_BLOCKING_LAYERS = ["pipeline_item_type", "pipeline_transition", "mailbox_processing_mode", "mailbox_capability"];
const POLICY_SOURCES = ["mailbox", "fail_closed"];
const TRIGGER_MODES = ["AUTO", "CONDITIONAL", "LAZY", "FAN_OUT_FAN_IN", "BATCH_CONVERGE", "DUAL_CONVERGE"];
const PERMISSION_ACTIONS = ["view", "claim", "process", "reassign", "send_as", "manage"];

function validateCapabilityConfig(value, context, requireAll = false) {
    const config = requireObject(value, context);
    Object.entries(config).forEach(([key, entryValue]) => {
        requireEnum(key, PROCESSING_CAPABILITIES, `${context} capability`);
        requireBoolean(entryValue, `${context}.${key}`);
    });
    if (requireAll) {
        PROCESSING_CAPABILITIES.forEach((capability) => {
            requireBooleanField(config, capability, context);
        });
    }
    return config;
}

function validateEffectiveProcessingPolicy(value, context) {
    const policy = requireObject(value, context);
    requireEnumField(policy, "mode", MAILBOX_PROCESSING_MODES, context);
    validateCapabilityConfig(readField(policy, "configured_capabilities", context), `${context}.configured_capabilities`);
    validateCapabilityConfig(readField(policy, "effective_capabilities", context), `${context}.effective_capabilities`, true);
    requireEnumField(policy, "source", POLICY_SOURCES, context);
    requireNullableStringField(policy, "reason", context);
    return policy;
}

export function validateOrganizationsPayload(payload, context = "组织列表") {
    const organizations = requireArray(payload, `${context} data`);
    organizations.forEach((organization, index) => {
        const itemContext = `${context}[${index}]`;
        const record = requireObject(organization, itemContext);
        requireStringField(record, "id", itemContext);
        requireStringField(record, "name", itemContext);
    });
    return organizations;
}

function validateMailboxListItem(mailbox, context) {
    const record = requireObject(mailbox, context);
    requireStringField(record, "id", context);
    requireStringField(record, "organization_id", context);
    requireStringField(record, "email_address", context);
    requireNullableStringField(record, "display_name", context);
    requireEnumField(record, "mailbox_type", MAILBOX_TYPES, context);
    requireBooleanField(record, "is_active", context);
    requireBooleanField(record, "is_receive_enabled", context);
    requireBooleanField(record, "is_poll_enabled", context);
    requireBooleanField(record, "is_platform_default", context);
    requireBooleanField(record, "has_credential", context);
    requireEnumField(record, "processing_mode", MAILBOX_PROCESSING_MODES, context);
    requireNullableDateStringField(record, "last_sync_at", context);
    requireNullableStringField(record, "last_poll_error", context);
    return record;
}

export function validateMailboxListPayload(payload) {
    const mailboxes = requireArray(payload, "收件箱列表 data");
    mailboxes.forEach((mailbox, index) => validateMailboxListItem(mailbox, `收件箱列表[${index}]`));
    return mailboxes;
}

export function validateMailboxDetailPayload(payload) {
    const mailbox = requireObject(payload, "邮箱详情 data");
    validateMailboxListItem(mailbox, "邮箱详情");
    requireNullableStringField(mailbox, "provider", "邮箱详情");
    requireNullableStringField(mailbox, "imap_host", "邮箱详情");
    requireNullableNumberField(mailbox, "imap_port", "邮箱详情");
    requireNullableStringField(mailbox, "smtp_host", "邮箱详情");
    requireNullableNumberField(mailbox, "smtp_port", "邮箱详情");
    requireBooleanField(mailbox, "is_send_enabled", "邮箱详情");
    requirePositiveIntegerField(mailbox, "poll_interval_seconds", "邮箱详情");
    requirePositiveIntegerField(mailbox, "fetch_since_days", "邮箱详情");
    requirePositiveIntegerField(mailbox, "max_fetch_per_round", "邮箱详情");
    requireBooleanField(mailbox, "store_attachments_in_oss", "邮箱详情");
    requireBooleanField(mailbox, "store_inline_images_in_oss", "邮箱详情");
    validateCapabilityConfig(readField(mailbox, "processing_capabilities", "邮箱详情"), "邮箱详情.processing_capabilities");
    validateEffectiveProcessingPolicy(readField(mailbox, "effective_processing_policy", "邮箱详情"), "邮箱详情.effective_processing_policy");
    requireNullableDateStringField(mailbox, "processing_policy_updated_at", "邮箱详情");
    requireNullableStringField(mailbox, "processing_policy_updated_by", "邮箱详情");
    return mailbox;
}

export function validateSchedulerStatusPayload(payload) {
    const status = requireObject(payload, "调度器状态 data");
    requireBooleanField(status, "running", "调度器状态");
    requireArray(readField(status, "mailboxes", "调度器状态"), "调度器状态.mailboxes").forEach((mailbox, index) => {
        const itemContext = `调度器状态.mailboxes[${index}]`;
        const record = requireObject(mailbox, itemContext);
        requireStringField(record, "mailboxId", itemContext);
        requireStringField(record, "organizationId", itemContext);
        requireStringField(record, "emailAddress", itemContext);
        requireNonNegativeIntegerField(record, "intervalMs", itemContext);
        requireBooleanField(record, "isPolling", itemContext);
    });
    return status;
}

function validateSystemStats(value, context) {
    const stats = requireObject(value, context);
    [
        "totalMessages",
        "totalReceipts",
        "totalProcessingEvents",
        "pendingInboundEvents",
        "failedEvents",
        "pendingOutboundEvents",
        "activeWorkItems",
    ].forEach((key) => requireNonNegativeIntegerField(stats, key, context));
    return stats;
}

export function validateOpsStatsPayload(payload) {
    const dashboard = requireObject(payload, "运维统计 data");
    return validateSystemStats(readField(dashboard, "stats", "运维统计"), "运维统计.stats");
}

export function validateIngestionSummaryPayload(payload) {
    const summary = requireObject(payload, "同步结果 data");
    requireNonNegativeIntegerField(summary, "fetchedCount", "同步结果");
    return summary;
}

export function validateTestConnectionPayload(payload) {
    const result = requireObject(payload, "连接测试 data");
    requireBooleanField(result, "success", "连接测试");
    requireStringField(result, "message", "连接测试");
    if (hasOwn(result, "folders") && result.folders !== undefined) {
        requireStringArray(result.folders, "连接测试.folders");
    }
    return result;
}

export function validateMailboxIngestSummaryPayload(payload) {
    const summary = requireObject(payload, "近期活动 data");
    requireStringField(summary, "mailboxId", "近期活动");
    requireNonNegativeIntegerField(summary, "recentReceiptCount", "近期活动");
    requireNonNegativeIntegerField(summary, "recentProcessingEventCount", "近期活动");
    requireNonNegativeIntegerField(summary, "recentFailedCount", "近期活动");
    return summary;
}

export function validateMailboxPendingSummaryPayload(payload) {
    const summary = requireObject(payload, "处理积压摘要 data");
    requireStringField(summary, "mailboxId", "处理积压摘要");
    requireNonNegativeIntegerField(summary, "total", "处理积压摘要");
    requireArray(readField(summary, "byStatus", "处理积压摘要"), "处理积压摘要.byStatus").forEach((item, index) => {
        const itemContext = `处理积压摘要.byStatus[${index}]`;
        const record = requireObject(item, itemContext);
        requireEnumField(record, "status", PROCESSING_STATUSES, itemContext);
        requireNonNegativeIntegerField(record, "count", itemContext);
    });
    return summary;
}

function validatePipelinePolicyOverride(value, context) {
    if (value === null) {
        return value;
    }
    const override = requireObject(value, context);
    requireEnumField(override, "status", PIPELINE_POLICY_STATUSES, context);
    requireNullableStringField(override, "reason", context);
    requireStringField(override, "updated_by", context);
    requireDateStringField(override, "updated_at", context);
    requireDateStringField(override, "created_at", context);
    return override;
}

function validatePipelineGate(value, context) {
    const gate = requireObject(value, context);
    requireBooleanField(gate, "allowed", context);
    requireEnumField(gate, "target_kind", PIPELINE_TARGET_KINDS, context);
    requireStringField(gate, "target_id", context);
    requireEnumField(gate, "status", PIPELINE_POLICY_STATUSES, context);
    requireEnumField(gate, "default_status", PIPELINE_POLICY_STATUSES, context);
    requireNullableStringField(gate, "reason", context);
    requireEnumField(gate, "stage", PIPELINE_STAGES, context);
    requireEnumField(gate, "policy_source", PIPELINE_POLICY_SOURCES, context);
    validatePipelinePolicyOverride(readField(gate, "policy_override", context), `${context}.policy_override`);
    return gate;
}

export function validateBjaAvailabilityPayload(payload) {
    const availability = requireObject(payload, "BJA 可用性 data");
    requireEnumField(availability, "capability", ["business_judgment"], "BJA 可用性");
    requireEnumField(availability, "item_type", ["business_judgment"], "BJA 可用性");
    requireStringField(availability, "transition_id", "BJA 可用性");
    requireStringField(availability, "mailbox_id", "BJA 可用性");
    requireNullableStringField(availability, "receipt_id", "BJA 可用性");
    requireBooleanField(availability, "available", "BJA 可用性");
    requireBooleanField(availability, "manual_trigger_available", "BJA 可用性");
    requireNullableEnumField(availability, "blocking_layer", BJA_BLOCKING_LAYERS, "BJA 可用性");
    requireNullableEnumField(availability, "manual_trigger_blocking_layer", BJA_BLOCKING_LAYERS.filter((item) => item !== "pipeline_transition"), "BJA 可用性");
    requireArray(readField(availability, "blocked_by", "BJA 可用性"), "BJA 可用性.blocked_by")
        .forEach((item, index) => requireEnum(item, BJA_BLOCKING_LAYERS, `BJA 可用性.blocked_by[${index}]`));
    validatePipelineGate(readField(availability, "item_type_gate", "BJA 可用性"), "BJA 可用性.item_type_gate");
    validatePipelineGate(readField(availability, "transition_gate", "BJA 可用性"), "BJA 可用性.transition_gate");

    const mailboxGate = requireObject(readField(availability, "mailbox_gate", "BJA 可用性"), "BJA 可用性.mailbox_gate");
    requireBooleanField(mailboxGate, "allowed", "BJA 可用性.mailbox_gate");
    requireEnumField(mailboxGate, "capability", ["business_judgment"], "BJA 可用性.mailbox_gate");
    requireNullableStringField(mailboxGate, "reason", "BJA 可用性.mailbox_gate");
    requireEnumField(mailboxGate, "processing_mode", MAILBOX_PROCESSING_MODES, "BJA 可用性.mailbox_gate");
    requireBooleanField(mailboxGate, "configured_enabled", "BJA 可用性.mailbox_gate");
    requireBooleanField(mailboxGate, "effective_enabled", "BJA 可用性.mailbox_gate");
    requireEnumField(mailboxGate, "policy_source", POLICY_SOURCES, "BJA 可用性.mailbox_gate");
    requireNullableStringField(mailboxGate, "policy_reason", "BJA 可用性.mailbox_gate");
    requireNullableStringField(mailboxGate, "updated_by", "BJA 可用性.mailbox_gate");
    requireNullableDateStringField(mailboxGate, "updated_at", "BJA 可用性.mailbox_gate");
    validateEffectiveProcessingPolicy(readField(mailboxGate, "policy", "BJA 可用性.mailbox_gate"), "BJA 可用性.mailbox_gate.policy");
    return availability;
}

function validateWorkItem(value, context) {
    const wi = requireObject(value, context);
    requireStringField(wi, "id", context);
    requireStringField(wi, "target_type", context);
    requireStringField(wi, "target_id", context);
    requireNullableStringField(wi, "mailbox_receipt_id", context);
    requireNullableStringField(wi, "classification_id", context);
    requireStringField(wi, "item_type", context);
    requireNullableStringField(wi, "title", context);
    requireNullableStringField(wi, "description", context);
    requireEnumField(wi, "processing_mode", WORK_ITEM_PROCESSING_MODES, context);
    requireNullableEnumField(wi, "execution_kind", EXECUTION_KINDS, context);
    requireNullableEnumField(wi, "pool_profile", POOL_PROFILES, context);
    requireEnumField(wi, "status", WORK_ITEM_STATUSES, context);
    requireNumberField(wi, "priority", context);
    requireNullableStringField(wi, "assigned_to", context);
    requireNullableDateStringField(wi, "assigned_at", context);
    requireNullableStringField(wi, "locked_by", context);
    requireNullableDateStringField(wi, "locked_at", context);
    requireNumberField(wi, "version", context);
    requireNullableStringField(wi, "result_summary", context);
    requireNullableDateStringField(wi, "completed_at", context);
    requireNonNegativeIntegerField(wi, "max_retries", context);
    requireNonNegativeIntegerField(wi, "retry_count", context);
    requireNullableStringField(wi, "agent_task_id", context);
    requireNullableStringField(wi, "agent_dispatch_attempt_id", context);
    requireNullableStringField(wi, "execution_attempt_id", context);
    requireNullableDateStringField(wi, "dispatched_at", context);
    requireNullableStringField(wi, "trigger_source_id", context);
    requireNullableStringField(wi, "transition_id", context);
    requireNullableStringField(wi, "fan_out_group_id", context);
    requireNullableNumberField(wi, "fan_out_index", context);
    requireNullableStringField(wi, "idempotency_key", context);
    requirePresentField(wi, "metadata", context);
    requirePresentField(wi, "result_data", context);
    requireDateStringField(wi, "created_at", context);
    requireDateStringField(wi, "updated_at", context);
    return wi;
}

function validateReceiptSummary(value, context) {
    const receipt = requireObject(value, context);
    requireStringField(receipt, "id", context);
    requireStringField(receipt, "canonical_message_id", context);
    requireStringField(receipt, "mailbox_id", context);
    requireStringField(receipt, "organization_id", context);
    requireStringField(receipt, "direction", context);
    requireNullableStringField(receipt, "remote_uid", context);
    requireNullableStringField(receipt, "remote_folder", context);
    requireStringField(receipt, "collab_status", context);
    requireNullableStringField(receipt, "delivered_to", context);
    requireNullableDateStringField(receipt, "received_at", context);
    requirePresentField(receipt, "processing_flags", context);
    requireDateStringField(receipt, "created_at", context);
    requireDateStringField(receipt, "updated_at", context);
    return receipt;
}

function validateCanonicalMessageSummary(value, context) {
    const message = requireObject(value, context);
    requireStringField(message, "id", context);
    requireNullableStringField(message, "thread_id", context);
    requireStringField(message, "message_id", context);
    requireNullableStringField(message, "subject", context);
    requireStringField(message, "from_address", context);
    requireNullableStringField(message, "from_display", context);
    requirePresentField(message, "to_addresses", context);
    requireNullableDateStringField(message, "date", context);
    requirePresentField(message, "processing_flags", context);
    requireDateStringField(message, "created_at", context);
    requireDateStringField(message, "updated_at", context);
    return message;
}

function validateWorkItemEnvelope(value, context) {
    const envelope = requireObject(value, context);
    validateWorkItem(readField(envelope, "work_item", context), `${context}.work_item`);
    validateReceiptSummary(readField(envelope, "receipt_summary", context), `${context}.receipt_summary`);
    validateCanonicalMessageSummary(readField(envelope, "canonical_message_summary", context), `${context}.canonical_message_summary`);
    return envelope;
}

export function validateWorkItemListPayload(payload) {
    const data = requireObject(payload, "Work Item 列表 data");
    requireArray(readField(data, "items", "Work Item 列表"), "Work Item 列表.items")
        .forEach((item, index) => validateWorkItemEnvelope(item, `Work Item 列表.items[${index}]`));
    const pagination = requireObject(readField(data, "pagination", "Work Item 列表"), "Work Item 列表.pagination");
    requirePositiveIntegerField(pagination, "page", "Work Item 列表.pagination");
    requirePositiveIntegerField(pagination, "pageSize", "Work Item 列表.pagination");
    requireNonNegativeIntegerField(pagination, "total", "Work Item 列表.pagination");
    requireNonNegativeIntegerField(pagination, "totalPages", "Work Item 列表.pagination");
    return data;
}

function validateProcessingEvent(value, context) {
    const event = requireObject(value, context);
    requireStringField(event, "id", context);
    requireEnumField(event, "status", PROCESSING_STATUSES, context);
    requireStringField(event, "step", context);
    requireNullableStringField(event, "error_message", context);
    requireDateStringField(event, "created_at", context);
    requireNullableDateStringField(event, "completed_at", context);
    return event;
}

function validateDeadLetter(value, context) {
    const item = requireObject(value, context);
    requireStringField(item, "id", context);
    requireDateStringField(item, "moved_to_dead_at", context);
    requireNullableNumberField(item, "retry_count", context);
    requireNullableDateStringField(item, "acknowledged_at", context);
    requireNullableStringField(item, "error_message", context);
    requireNullableStringField(item, "resolution", context);
    return item;
}

function validateClassification(value, context) {
    const item = requireObject(value, context);
    requireEnumField(item, "classification_kind", CLASSIFICATION_KINDS, context);
    requireStringField(item, "category", context);
    requireNullableStringField(item, "sub_category", context);
    requireNullableNumberField(item, "confidence", context);
    requireBooleanField(item, "is_active", context);
    requireDateStringField(item, "created_at", context);
    return item;
}

function validateApfmClues(value, context) {
    const clues = requireObject(value, context);
    requireObject(readField(clues, "direct_fields", context), `${context}.direct_fields`);
    requireArray(readField(clues, "extracted_ids", context), `${context}.extracted_ids`).forEach((item, index) => {
        const itemContext = `${context}.extracted_ids[${index}]`;
        const record = requireObject(item, itemContext);
        requireStringField(record, "source", itemContext);
        requireStringField(record, "key", itemContext);
        requireStringField(record, "value", itemContext);
    });
    requireStringField(clues, "limitation", context);
    return clues;
}

export function validateWorkItemDetailPayload(payload) {
    const detail = requireObject(payload, "Work Item 详情 data");
    validateWorkItem(readField(detail, "work_item", "Work Item 详情"), "Work Item 详情.work_item");
    requireArray(readField(detail, "dead_letters", "Work Item 详情"), "Work Item 详情.dead_letters")
        .forEach((item, index) => validateDeadLetter(item, `Work Item 详情.dead_letters[${index}]`));
    requireArray(readField(detail, "processing_events", "Work Item 详情"), "Work Item 详情.processing_events")
        .forEach((item, index) => validateProcessingEvent(item, `Work Item 详情.processing_events[${index}]`));
    validateReceiptSummary(readField(detail, "receipt_summary", "Work Item 详情"), "Work Item 详情.receipt_summary");
    validateCanonicalMessageSummary(readField(detail, "canonical_message_summary", "Work Item 详情"), "Work Item 详情.canonical_message_summary");
    requireArray(readField(detail, "classifications", "Work Item 详情"), "Work Item 详情.classifications")
        .forEach((item, index) => validateClassification(item, `Work Item 详情.classifications[${index}]`));
    validateApfmClues(readField(detail, "apfm_clues", "Work Item 详情"), "Work Item 详情.apfm_clues");
    return detail;
}

function validateRuleConditionValue(value, context) {
    if (value === null || typeof value === "string" || typeof value === "boolean") {
        return value;
    }
    if (Array.isArray(value)) {
        value.forEach((item, index) => requireString(item, `${context}[${index}]`));
        return value;
    }
    throw contractError(`${context} 必须是字符串、字符串数组、布尔值或 null`);
}

function validateClassificationRuleDefinition(value, context) {
    const definition = requireObject(value, context);
    const rule = requireObject(readField(definition, "rule", context), `${context}.rule`);
    requireStringField(rule, "id", `${context}.rule`);
    requireStringField(rule, "name", `${context}.rule`);
    requireNullableStringField(rule, "description", `${context}.rule`);
    requireNumberField(rule, "priority", `${context}.rule`);
    requireBooleanField(rule, "is_enabled", `${context}.rule`);
    requireEnumField(rule, "scope_type", CLASSIFICATION_SCOPES, `${context}.rule`);
    requireNullableStringField(rule, "organization_id", `${context}.rule`);
    requireNullableStringField(rule, "mailbox_id", `${context}.rule`);
    requireNullableObjectField(rule, "metadata", `${context}.rule`);
    requireDateStringField(rule, "updated_at", `${context}.rule`);

    requireArray(readField(definition, "conditions", context), `${context}.conditions`).forEach((condition, index) => {
        const itemContext = `${context}.conditions[${index}]`;
        const record = requireObject(condition, itemContext);
        requireNumberField(record, "condition_group", itemContext);
        requireNumberField(record, "sort_order", itemContext);
        requireEnumField(record, "field", CLASSIFICATION_FIELDS, itemContext);
        requireEnumField(record, "operator", CLASSIFICATION_OPERATORS, itemContext);
        validateRuleConditionValue(readField(record, "value", itemContext), `${itemContext}.value`);
    });

    const action = requireObject(readField(definition, "action", context), `${context}.action`);
    requireStringField(action, "category", `${context}.action`);
    requireNullableStringField(action, "sub_category", `${context}.action`);
    requireNullableStringField(action, "suggested_queue", `${context}.action`);
    requireEnumField(action, "suggested_mode", WORK_ITEM_PROCESSING_MODES, `${context}.action`);
    requireNullableNumberField(action, "confidence", `${context}.action`);
    return definition;
}

export function validateClassificationRulesPayload(payload) {
    const rules = requireArray(payload, "分类规则 data");
    rules.forEach((definition, index) => validateClassificationRuleDefinition(definition, `分类规则[${index}]`));
    return rules;
}

function validateClassificationMatchResult(value, context) {
    const result = requireObject(value, context);
    requireStringField(result, "category", context);
    requireNullableStringField(result, "sub_category", context);
    requireNumberField(result, "confidence", context);
    requireStringField(result, "method", context);
    requireEnumField(result, "suggested_mode", WORK_ITEM_PROCESSING_MODES, context);
    requireNullableStringField(result, "suggested_queue", context);
    requireNullableStringField(result, "rule_id", context);
    requireNullableStringField(result, "rule_name", context);
    requireNullableEnumField(result, "rule_scope_type", CLASSIFICATION_SCOPES, context);
    requireNullableNumberField(result, "rule_priority", context);
    requireNullableDateStringField(result, "rule_updated_at", context);
    requireArray(readField(result, "trace", context), `${context}.trace`).forEach((item, index) => {
        const itemContext = `${context}.trace[${index}]`;
        const record = requireObject(item, itemContext);
        requireStringField(record, "rule_id", itemContext);
        requireStringField(record, "rule_name", itemContext);
        requireNumberField(record, "priority", itemContext);
        requireEnumField(record, "scope_type", CLASSIFICATION_SCOPES, itemContext);
        requireBooleanField(record, "matched", itemContext);
    });
    return result;
}

export function validateClassificationRuleTestPayload(payload) {
    const testResult = requireObject(payload, "规则测试 data");
    const input = requireObject(readField(testResult, "input", "规则测试"), "规则测试.input");
    requireNullableStringField(input, "subject", "规则测试.input");
    requireNullableStringField(input, "from_address", "规则测试.input");
    requireNullableStringField(input, "from_display", "规则测试.input");
    requireStringArray(readField(input, "to_addresses", "规则测试.input"), "规则测试.input.to_addresses");
    requireStringArray(readField(input, "attachment_filenames", "规则测试.input"), "规则测试.input.attachment_filenames");
    requireBooleanField(input, "has_attachments", "规则测试.input");

    const outcome = requireObject(readField(testResult, "result", "规则测试"), "规则测试.result");
    validateClassificationMatchResult(readField(outcome, "primary_result", "规则测试.result"), "规则测试.result.primary_result");
    requireArray(readField(outcome, "secondary_results", "规则测试.result"), "规则测试.result.secondary_results")
        .forEach((item, index) => validateClassificationMatchResult(item, `规则测试.result.secondary_results[${index}]`));
    return testResult;
}

function validatePipelineRuntimeSummary(value, context) {
    const summary = requireObject(value, context);
    ["pending_count", "in_progress_count", "dead_count", "failed_count_24h", "completed_count_24h"].forEach((key) => requireNonNegativeIntegerField(summary, key, context));
    requireNumberField(summary, "failure_rate_24h", context);
    requireNullableStringField(summary, "latest_error_summary", context);
    requireNullableDateStringField(summary, "latest_error_at", context);
    return summary;
}

function validateTransitionRuntimeSummary(value, context) {
    const summary = requireObject(value, context);
    requireNonNegativeIntegerField(summary, "open_count", context);
    requireNonNegativeIntegerField(summary, "created_count_24h", context);
    requireNullableDateStringField(summary, "latest_created_at", context);
    return summary;
}

function validatePipelineItemType(value, context) {
    const item = requireObject(value, context);
    requireStringField(item, "item_type", context);
    requireStringField(item, "display_name", context);
    requireEnumField(item, "group", PIPELINE_GROUPS, context);
    requireEnumField(item, "status", PIPELINE_POLICY_STATUSES, context);
    requireEnumField(item, "default_status", PIPELINE_POLICY_STATUSES, context);
    validatePipelinePolicyOverride(readField(item, "policy_override", context), `${context}.policy_override`);
    requireBooleanField(item, "allow_create", context);
    requireBooleanField(item, "allow_execute", context);
    requireEnumField(item, "execution_kind", EXECUTION_KINDS, context);
    requireNullableEnumField(item, "pool_profile", POOL_PROFILES, context);
    requireNonNegativeIntegerField(item, "max_concurrency", context);
    requireNonNegativeIntegerField(item, "timeout_seconds", context);
    requireNonNegativeIntegerField(item, "bp_threshold", context);
    requireNullableStringField(item, "flag_key", context);
    requireNullableStringField(item, "capability", context);
    validatePipelineRuntimeSummary(readField(item, "runtime_summary", context), `${context}.runtime_summary`);
    return item;
}

function validatePipelineTransition(value, context) {
    const transition = requireObject(value, context);
    requireStringField(transition, "transition_id", context);
    requireStringField(transition, "display_name", context);
    requireEnumField(transition, "status", PIPELINE_POLICY_STATUSES, context);
    requireEnumField(transition, "default_status", PIPELINE_POLICY_STATUSES, context);
    validatePipelinePolicyOverride(readField(transition, "policy_override", context), `${context}.policy_override`);
    requireBooleanField(transition, "allow_create", context);
    requireStringArray(readField(transition, "from_item_types", context), `${context}.from_item_types`);
    requireStringArray(readField(transition, "to_item_types", context), `${context}.to_item_types`);
    requireEnumField(transition, "trigger_mode", TRIGGER_MODES, context);
    requireNullableObjectField(transition, "condition", context);
    requireNullableStringField(transition, "target_scope", context);
    requireNumberField(transition, "priority", context);
    validateTransitionRuntimeSummary(readField(transition, "runtime_summary", context), `${context}.runtime_summary`);
    return transition;
}

export function validatePipelineGovernancePayload(payload) {
    const data = requireObject(payload, "Pipeline 治理 data");
    requireArray(readField(data, "item_types", "Pipeline 治理"), "Pipeline 治理.item_types")
        .forEach((item, index) => validatePipelineItemType(item, `Pipeline 治理.item_types[${index}]`));
    requireArray(readField(data, "transitions", "Pipeline 治理"), "Pipeline 治理.transitions")
        .forEach((item, index) => validatePipelineTransition(item, `Pipeline 治理.transitions[${index}]`));
    const summary = requireObject(readField(data, "summary", "Pipeline 治理"), "Pipeline 治理.summary");
    [
        "item_type_total",
        "item_type_active",
        "item_type_blocking",
        "transition_total",
        "transition_active",
        "transition_blocking",
    ].forEach((key) => requireNonNegativeIntegerField(summary, key, "Pipeline 治理.summary"));
    return data;
}

function validatePermissionGrant(value, context) {
    const grant = requireObject(value, context);
    requireStringField(grant, "id", context);
    requireStringField(grant, "organization_id", context);
    requireNullableStringField(grant, "mailbox_id", context);
    requireStringField(grant, "user_id", context);
    requireEnumField(grant, "action", PERMISSION_ACTIONS, context);
    requireNullableStringField(grant, "granted_by", context);
    requireNullableDateStringField(grant, "expires_at", context);
    requireBooleanField(grant, "is_active", context);
    requireDateStringField(grant, "created_at", context);
    requireDateStringField(grant, "updated_at", context);
    return grant;
}

export function validateMailboxPermissionsPayload(payload) {
    const data = requireObject(payload, "邮箱授权 data");
    const grants = requireArray(readField(data, "grants", "邮箱授权"), "邮箱授权.grants");
    grants.forEach((grant, index) => validatePermissionGrant(grant, `邮箱授权.grants[${index}]`));
    return data;
}
